import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SettingsNavRowProps {
  icon: ReactNode;
  label: string;
  description?: string;
  /**
   * When set, the row is an active pointer to that route. When omitted, the row
   * is a non-interactive placeholder awaiting a later ticket (#244 / #245).
   */
  to?: string;
}

const rowBase =
  'flex items-center gap-3 rounded-lg border px-4 py-3 text-left';

/**
 * One entry inside a Settings scope section — either a live pointer (a Link to
 * an existing surface, e.g. Finance → Settings) or a "Coming soon" placeholder
 * for a sub-section a later ticket will build out.
 */
export function SettingsNavRow({ icon, label, description, to }: SettingsNavRowProps) {
  const body = (
    <>
      <span className="text-muted-foreground shrink-0">{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium">{label}</span>
        {description && (
          <span className="block text-xs text-muted-foreground">{description}</span>
        )}
      </span>
    </>
  );

  if (to) {
    return (
      <Link
        to={to}
        className={cn(
          rowBase,
          'bg-card transition-colors hover:bg-accent hover:text-accent-foreground'
        )}
      >
        {body}
        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
      </Link>
    );
  }

  return (
    <div className={cn(rowBase, 'bg-muted/40 cursor-default')} aria-disabled="true">
      {body}
      <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
        Coming soon
      </span>
    </div>
  );
}
